import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  getLeaveTransactions,
  getLeaveTypes,
} from "../../api/api";

// =====================================================
// MAIN
// =====================================================

export default function TransactionHistory({

  userId,

  userName,

}) {

  const [transactions, setTransactions] =
    useState([]);

  const [leaveTypes, setLeaveTypes] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const [currentPage, setCurrentPage] =
    useState(1);

  const [filters, setFilters] =
    useState({

      search: "",

      transaction_type: "",

      leave_type_id: "",

      month: "",

      year:
        new Date().getFullYear(),
    });

  const rowsPerPage = 10;

  // =====================================================
  // INITIAL LOAD
  // =====================================================

  useEffect(() => {

    if (userId) {

      fetchTransactions();

      fetchLeaveTypes();
    }

  }, [userId]);

  // =====================================================
  // FETCH TRANSACTIONS
  // =====================================================

  const fetchTransactions =
    async () => {

      try {

        setLoading(true);

        const data =
          await getLeaveTransactions(
            userId
          );

        setTransactions(data || []);

      } catch (error) {

        console.error(error);

      } finally {

        setLoading(false);
      }
    };

  // =====================================================
  // FETCH LEAVE TYPES
  // =====================================================

  const fetchLeaveTypes =
    async () => {

      try {

        const data =
          await getLeaveTypes();

        setLeaveTypes(data || []);

      } catch (error) {

        console.error(error);
      }
    };

  // =====================================================
  // FILTERED TRANSACTIONS
  // =====================================================

  const filteredTransactions =
    useMemo(() => {

      return transactions.filter(
        (transaction) => {

          // SEARCH

          const matchesSearch =

            transaction.remarks
              ?.toLowerCase()
              .includes(
                filters.search
                  .toLowerCase()
              )

            ||

            transaction.leave_type_name
              ?.toLowerCase()
              .includes(
                filters.search
                  .toLowerCase()
              );

          // TYPE

          const matchesType =

            !filters.transaction_type ||

            transaction.transaction_type ===
            filters.transaction_type;

          // LEAVE TYPE

          const matchesLeaveType =

            !filters.leave_type_id ||

            String(
              transaction.leave_type_id
            ) ===
            String(
              filters.leave_type_id
            );

          // MONTH

          const transactionDate =
            new Date(
              transaction.created_at
            );

          const matchesMonth =

            !filters.month ||

            transactionDate.getMonth() + 1 ===
            Number(filters.month);

          // YEAR

          const matchesYear =

            transactionDate.getFullYear() ===
            Number(filters.year);

          return (

            matchesSearch &&
            matchesType &&
            matchesLeaveType &&
            matchesMonth &&
            matchesYear
          );
        }
      );

    }, [
      transactions,
      filters,
    ]);

  // =====================================================
  // PAGINATION
  // =====================================================

  const totalPages =
    Math.ceil(
      filteredTransactions.length /
      rowsPerPage
    ) || 1;

  const paginatedTransactions =
    useMemo(() => {

      const start =
        (currentPage - 1) *
        rowsPerPage;

      return filteredTransactions.slice(
        start,
        start + rowsPerPage
      );

    }, [
      filteredTransactions,
      currentPage,
    ]);

  // =====================================================
  // BADGE COLOR
  // =====================================================

  const getBadgeColor =
    (type) => {

      if (
        type?.includes("rollback")
      ) {

        return {
          background: "#dbeafe",
          color: "#1d4ed8",
        };
      }

      if (
        type?.includes("loss")
      ) {

        return {
          background: "#ffedd5",
          color: "#ea580c",
        };
      }

      if (
        type?.includes("deducted")
      ) {

        return {
          background: "#fee2e2",
          color: "#dc2626",
        };
      }

      return {
        background: "#dcfce7",
        color: "#16a34a",
      };
    };

  // =====================================================
  // UI
  // =====================================================

  return (

    <div style={styles.page}>

      {/* ================================================= */}
      {/* HEADER */}
      {/* ================================================= */}

      <div style={styles.header}>

        <div>

          <div style={styles.title}>
            Transaction History
          </div>

          <div style={styles.subtitle}>
            {userName}
          </div>

        </div>

      </div>

      {/* ================================================= */}
      {/* FILTERS */}
      {/* ================================================= */}

      <div style={styles.filterCard}>

        <div style={styles.filters}>

          {/* SEARCH */}

          <input
            type="text"

            placeholder="Search remarks or leave type"

            value={filters.search}

            onChange={(e) =>
              setFilters({

                ...filters,

                search:
                  e.target.value,
              })
            }

            style={styles.input}
          />

          {/* TRANSACTION TYPE */}

          <select
            value={filters.transaction_type}

            onChange={(e) =>
              setFilters({

                ...filters,

                transaction_type:
                  e.target.value,
              })
            }

            style={styles.input}
          >

            <option value="">
              All Transactions
            </option>

            <option value="planned_leave_deducted">
              Planned Leave Used
            </option>

            <option value="unplanned_leave_deducted">
              Unplanned Leave Used
            </option>

            <option value="loss_of_pay">
              Loss Of Pay
            </option>

            <option value="planned_leave_credit">
              Planned Credit
            </option>

            <option value="unplanned_leave_credit">
              Unplanned Credit
            </option>

            <option value="planned_leave_rollback">
              Planned Rollback
            </option>

            <option value="unplanned_leave_rollback">
              Unplanned Rollback
            </option>

          </select>

          {/* LEAVE TYPE */}

          <select
            value={filters.leave_type_id}

            onChange={(e) =>
              setFilters({

                ...filters,

                leave_type_id:
                  e.target.value,
              })
            }

            style={styles.input}
          >

            <option value="">
              All Leave Types
            </option>

            {leaveTypes.map((leaveType) => (

              <option
                key={leaveType.id}
                value={leaveType.id}
              >

                {leaveType.name}

              </option>
            ))}

          </select>

          {/* MONTH */}

          <select
            value={filters.month}

            onChange={(e) =>
              setFilters({

                ...filters,

                month:
                  e.target.value,
              })
            }

            style={styles.input}
          >

            <option value="">
              All Months
            </option>

            {[
              "January",
              "February",
              "March",
              "April",
              "May",
              "June",
              "July",
              "August",
              "September",
              "October",
              "November",
              "December",
            ].map((month, index) => (

              <option
                key={month}
                value={index + 1}
              >

                {month}

              </option>
            ))}

          </select>

          {/* YEAR */}

          <select
            value={filters.year}

            onChange={(e) =>
              setFilters({

                ...filters,

                year:
                  e.target.value,
              })
            }

            style={styles.input}
          >

            <option value="2026">
              2026
            </option>

            <option value="2025">
              2025
            </option>

            <option value="2024">
              2024
            </option>

          </select>

        </div>

      </div>

      {/* ================================================= */}
      {/* TABLE */}
      {/* ================================================= */}

      <div style={styles.card}>

        {loading ? (

          <div style={styles.loading}>
            Loading transactions...
          </div>

        ) : (

          <div style={styles.tableWrapper}>

            <table style={styles.table}>

              <thead>

                <tr>

                  <th style={styles.th}>
                    Date
                  </th>

                  <th style={styles.th}>
                    Leave Type
                  </th>

                  <th style={styles.th}>
                    Transaction
                  </th>

                  <th style={styles.th}>
                    Days
                  </th>

                  <th style={styles.th}>
                    Before
                  </th>

                  <th style={styles.th}>
                    After
                  </th>

                  <th style={styles.th}>
                    Remarks
                  </th>

                </tr>

              </thead>

              <tbody>

                {paginatedTransactions.map((transaction) => (

                  <tr
                    key={transaction.id}
                  >

                    <td style={styles.td}>

                      {
                        new Date(
                          transaction.created_at
                        ).toLocaleDateString()
                      }

                    </td>

                    <td style={styles.td}>
                      {
                        transaction.leave_type_name
                      }
                    </td>

                    <td style={styles.td}>

                      <span
                        style={{
                          ...styles.badge,
                          ...getBadgeColor(
                            transaction.transaction_type
                          ),
                        }}
                      >

                        {
                          transaction.transaction_label
                        }

                      </span>

                    </td>

                    <td style={styles.td}>
                      {transaction.days}
                    </td>

                    <td style={styles.td}>
                      {
                        transaction.balance_before
                      }
                    </td>

                    <td style={styles.td}>
                      {
                        transaction.balance_after
                      }
                    </td>

                    <td style={styles.td}>
                      {transaction.remarks}
                    </td>

                  </tr>
                ))}

              </tbody>

            </table>

          </div>
        )}

      </div>

      {/* ================================================= */}
      {/* PAGINATION */}
      {/* ================================================= */}

      <div style={styles.pagination}>

        <button
          style={styles.pageBtn}

          disabled={currentPage === 1}

          onClick={() =>
            setCurrentPage(
              currentPage - 1
            )
          }
        >
          ◀
        </button>

        <div style={styles.pageText}>

          {currentPage}
          {" / "}
          {totalPages}

        </div>

        <button
          style={styles.pageBtn}

          disabled={
            currentPage === totalPages
          }

          onClick={() =>
            setCurrentPage(
              currentPage + 1
            )
          }
        >
          ▶
        </button>

      </div>

    </div>
  );
}

// =====================================================
// STYLES
// =====================================================

const styles = {

  page: {

    padding: "24px",

    background: "#f8fafc",

    minHeight: "100vh",
  },

  header: {

    marginBottom: "24px",
  },

  title: {

    fontSize: "32px",

    fontWeight: "800",

    color: "#111827",
  },

  subtitle: {

    marginTop: "6px",

    color: "#6b7280",
  },

  filterCard: {

    background: "#fff",

    borderRadius: "18px",

    padding: "20px",

    border: "1px solid #e5e7eb",

    marginBottom: "24px",
  },

  filters: {

    display: "flex",

    flexWrap: "wrap",

    gap: "12px",
  },

  input: {

    padding: "12px 14px",

    borderRadius: "12px",

    border: "1px solid #d1d5db",

    minWidth: "180px",

    outline: "none",
  },

  card: {

    background: "#fff",

    borderRadius: "18px",

    border: "1px solid #e5e7eb",

    overflow: "hidden",
  },

  loading: {

    textAlign: "center",

    padding: "50px",

    color: "#6b7280",
  },

  tableWrapper: {

    overflowX: "auto",
  },

  table: {

    width: "100%",

    borderCollapse: "collapse",
  },

  th: {

    textAlign: "left",

    padding: "16px",

    background: "#f9fafb",

    borderBottom: "1px solid #e5e7eb",

    fontWeight: "700",
  },

  td: {

    padding: "16px",

    borderBottom: "1px solid #f1f5f9",
  },

  badge: {

    padding: "6px 12px",

    borderRadius: "999px",

    fontSize: "12px",

    fontWeight: "700",
  },

  pagination: {

    marginTop: "24px",

    display: "flex",

    justifyContent: "center",

    alignItems: "center",

    gap: "14px",
  },

  pageBtn: {

    width: "42px",

    height: "42px",

    borderRadius: "12px",

    border: "1px solid #d1d5db",

    background: "#fff",

    cursor: "pointer",
  },

  pageText: {

    fontWeight: "700",
  },
};